import styled from '@emotion/styled';
import { Chip, Table, TableBody, TableCell, TableRow as _TableRow, Tooltip, Typography } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC, Fragment } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import ProductPricing from '../../../../entity/ProductPricing';
import { AvailableDeliveryDueDate, MatchingFormat } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';

type Props = {
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  availableDeliveryDueDates: ReadonlyArray<AvailableDeliveryDueDate>;
  deliveryDueDays: number | null;
  expressDuePointTotal?: number;
  matchingFormat?: MatchingFormat;
  className?: string;
};

const DeliveryConditionsList: FC<Props> = ({
  isPointRequired,
  productPricing,
  availableDeliveryDueDates,
  deliveryDueDays,
  expressDuePointTotal,
  matchingFormat,
  className,
}) => {
  const targetDueDays = deliveryDueDays
    ? availableDeliveryDueDates.find((deliveryDueDate) => deliveryDueDate.days === deliveryDueDays)
    : null;

  return (
    <Root className={className}>
      <Table>
        <TableBody>
          {/* 納品日の列 */}
          <TableRow className={isPointRequired ? 'hasPoint' : ''}>
            <LeftTableCellWithoutBorder>
              <Typography variant="body2">
                <FormattedMessage id="creative_format_editor.creation_set_list.conditions.due_date" />
              </Typography>
              <Tooltip
                title={<FormattedMessage id="creation_set_list.express_delivery.help" />}
                placement="top-start"
                arrow
              >
                <FormattedMessage id="help.creation_list_delivery_due_days">
                  {(url: string) => (
                    <HelpAnchor href={url} target="_blank" rel="noopener noreferrer">
                      <HelpIcon />
                    </HelpAnchor>
                  )}
                </FormattedMessage>
              </Tooltip>
            </LeftTableCellWithoutBorder>
            <DenserTableCellWithoutBorder>
              {targetDueDays ? (
                <DueDays>
                  <Typography variant="body2">
                    <FormattedDate
                      value={targetDueDays.dueDate}
                      year="numeric"
                      month="short"
                      day="2-digit"
                      weekday="narrow"
                    />
                  </Typography>

                  {isPointRequired ? (
                    <>
                      <Spacer />
                      <Small>
                        {targetDueDays.expressDueDays ? (
                          <FormattedMessage
                            id={'creative_format_editor.creation_set_list.express_delivery'}
                            values={{
                              days: targetDueDays.expressDueDays,
                            }}
                          >
                            {(text: string) => <Chip label={text} />}
                          </FormattedMessage>
                        ) : null}
                      </Small>
                    </>
                  ) : null}
                </DueDays>
              ) : (
                '-'
              )}
            </DenserTableCellWithoutBorder>
            {isPointRequired && productPricing ? (
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint>
                    {productPricing.currency}
                    {((expressDuePointTotal || 0) * productPricing.flatRate).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            ) : null}
          </TableRow>
          {/* マッチング形式の列 */}
          <TableRow>
            <LeftTableCellWithoutBorder>
              <Typography variant="body2">
                <FormattedMessage id="creative_format_editor.creation_set_list.conditions.matching_format" />
              </Typography>
              {/*マッチング形式選択はまだ使わないのでヘルプをコメントアウトしておく。今後使われる想定。*/}
              {/*<Tooltip*/}
              {/*  title={<FormattedMessage id="project_editing_page.matching_format_setting.tool_tip.title" />}*/}
              {/*  placement="top-start"*/}
              {/*  arrow*/}
              {/*>*/}
              {/*  <FormattedMessage id="help.matching_format">*/}
              {/*    {(url: string) => (*/}
              {/*      <HelpAnchor href={url} target="_blank" rel="noopener noreferrer">*/}
              {/*        <HelpIcon />*/}
              {/*      </HelpAnchor>*/}
              {/*    )}*/}
              {/*  </FormattedMessage>*/}
              {/*</Tooltip>*/}
            </LeftTableCellWithoutBorder>
            <DenserTableCellWithoutBorder>
              <Typography variant="body2">
                <FormattedMessage id={`creative_format_editor.creation_set_list.matching_format.${matchingFormat}`} />
              </Typography>
            </DenserTableCellWithoutBorder>
          </TableRow>
        </TableBody>
      </Table>
    </Root>
  );
};

const Root = styled('div')({});

const TableRow = styled(_TableRow)({
  [mq('sp')]: {
    '&.hasPoint': {
      verticalAlign: 'baseline',
    },
  },
});

const DenserTableCell = styled(TableCell)();
DenserTableCell.defaultProps = { padding: 'default' };

const DenserTableCellWithoutBorder = styled(DenserTableCell)({
  borderBottom: 0,
});

const LeftTableCellWithoutBorder = styled(DenserTableCell)({
  display: 'flex',
  borderBottom: 0,
});

const AlignRight = styled('div')({
  textAlign: 'right',
});

const DueDays = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'flex-start',
  }),
  {
    [mq('sp')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
);

const RequiredPoint = styled(Typography)({});
RequiredPoint.defaultProps = { variant: 'body1' };

const Spacer = styled('span')({
  marginRight: SPACING_UNIT * 2,
});

const Small = styled('small')({
  [mq('sp')]: {
    marginTop: '5px',
  },
});

const HelpAnchor = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.secondary.main,
  textDecoration: 'none',
  marginLeft: SPACING_UNIT,
});

const HelpIcon = styled(Help)({
  marginRight: 2,
  fontSize: 20,
});

export default DeliveryConditionsList;
